import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, Users, TrendingUp } from 'lucide-react';
import { TrendingGames } from '../components/TrendingGames';
import { SearchBar } from '../components/SearchBar';
import { useGames } from '../hooks/useGames';

export function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'game' | 'user'>('game');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const navigate = useNavigate();
  const { games, isLoading } = useGames(null, searchType === 'game' ? searchQuery : '');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    if (searchType === 'game') {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate(`/user/${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const topGames = games
    .sort((a, b) => b.playerCount - a.playerCount)
    .slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-3 mb-8">
            <Gamepad2 className="w-12 h-12 text-indigo-600" />
            <h1 className="text-4xl font-bold text-gray-900">
              Steam Game Tracker
            </h1>
          </div>

          <div className="w-full max-w-2xl relative">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              searchType={searchType}
              onSearchTypeChange={setSearchType}
              onSubmit={handleSearch}
            />

            {isSearchFocused && !isLoading && searchType === 'game' && topGames.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[400px] overflow-y-auto">
                <div className="p-2 text-sm text-gray-500 border-b border-gray-100">
                  Top Games by Current Players
                </div>
                {topGames.map((game) => (
                  <div
                    key={game.id}
                    onClick={() => navigate(`/game/${game.id}`)}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-10 h-10 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {game.name}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="w-4 h-4 mr-1" />
                        {game.playerCount.toLocaleString()} players
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/trending')}
            className="mt-4 flex items-center justify-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium mx-auto group"
          >
            <TrendingUp className="w-5 h-5 transition-transform group-hover:scale-110" />
            <span>View Trending Games</span>
          </button>

          <div className="mt-16 w-full">
            {!isLoading && <TrendingGames games={games} onSelectGame={(id) => navigate(`/game/${id}`)} />}
          </div>
        </div>
      </div>
    </div>
  );
}